import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatNumberFixed } from "../../../../utils/numberUtils";
import { EnrichedPriceRow, formatUtc } from "./types";

type StatusFilter = "all" | "ok" | "failed" | "overridden" | "pending";

const fmt2 = (v: string) => {
  const n = parseFloat(v);
  return isNaN(n) ? v : formatNumberFixed(n, 2);
};

interface ColumnFilters {
  datetime: string;
  open: string;
  high: string;
  low: string;
  close: string;
  status: StatusFilter;
}

const EMPTY_FILTERS: ColumnFilters = {
  datetime: "",
  open: "",
  high: "",
  low: "",
  close: "",
  status: "all",
};

interface PriceTableProps {
  rows: EnrichedPriceRow[];
  onToggleRow: (slot: string) => void;
  onToggleAll: (visibleSlots: string[]) => void;
  selectedSlots: Set<string>;
  onDragApply: (price: string, targetSlots: string[]) => void;
  onInlineEdit: (slot: string, price: string) => void;
  onMultiplier: (slot: string, factor: number) => void;
  multValues: Record<string, string>;
  onMultChange: (slot: string, raw: string) => void;
  onVisibleSlotsChange?: (slots: string[]) => void;
}

const statusDot = (row: EnrichedPriceRow) => {
  if (row.pendingValue) return "bg-green-500";
  if (row.isOverridden) return "bg-rp-amber";
  if (row.isFailed) return "bg-red-500";
  return "bg-green-500 opacity-35";
};

const statusLabel = (row: EnrichedPriceRow): string => {
  if (row.pendingValue) return "Pending";
  if (row.isOverridden) return "Overridden";
  if (row.isFailed) return "Failed";
  return "OK";
};

const STATUS_OPTIONS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "ok", label: "OK" },
  { key: "failed", label: "Failed" },
  { key: "overridden", label: "Overridden" },
  { key: "pending", label: "Pending" },
];

function matchesText(value: string, filter: string): boolean {
  if (!filter) return true;
  return value.toLowerCase().includes(filter.toLowerCase());
}

function matchesStatus(row: EnrichedPriceRow, status: StatusFilter): boolean {
  if (status === "all") return true;
  const label = statusLabel(row).toLowerCase();
  return label === status;
}

function applyFilters(rows: EnrichedPriceRow[], filters: ColumnFilters): EnrichedPriceRow[] {
  return rows.filter((r) => {
    if (!matchesText(formatUtc(r.datetimeUtc), filters.datetime)) return false;
    if (!matchesText(r.open, filters.open)) return false;
    if (!matchesText(r.high, filters.high)) return false;
    if (!matchesText(r.low, filters.low)) return false;
    if (!matchesText(r.close, filters.close)) return false;
    if (!matchesStatus(r, filters.status)) return false;
    return true;
  });
}

const PriceTable: React.FC<PriceTableProps> = ({
  rows,
  onToggleRow,
  onToggleAll,
  selectedSlots,
  onDragApply,
  onInlineEdit,
  onMultiplier,
  multValues,
  onMultChange,
  onVisibleSlotsChange,
}) => {
  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [dragPrice, setDragPrice] = useState<string | null>(null);
  const dragTargets = useRef<Set<string>>(new Set());

  const fillSource = useRef<{
    type: "mult" | "override";
    value: string;
    startIdx: number;
  } | null>(null);
  const fillEndRef = useRef<number | null>(null);
  const [fillEndIdx, setFillEndIdx] = useState<number | null>(null);

  const visible = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const visibleSlots = useMemo(() => visible.map((r) => r.datetimeUtc), [visible]);
  const allSelected = visibleSlots.length > 0 && visibleSlots.every((s) => selectedSlots.has(s));

  useLayoutEffect(() => {
    onVisibleSlotsChange?.(visibleSlots);
  }, [visibleSlots, onVisibleSlotsChange]);

  const setFilter = useCallback(
    <K extends keyof ColumnFilters>(key: K, value: ColumnFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, price: string) => {
      setDragPrice(price);
      dragTargets.current.clear();
      e.dataTransfer.setData("text/plain", price);
      e.dataTransfer.effectAllowed = "copy";
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, slot: string, isFailed: boolean) => {
      if (!isFailed) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      dragTargets.current.add(slot);
    },
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const price = e.dataTransfer.getData("text/plain") || dragPrice;
      if (price && dragTargets.current.size > 0) {
        onDragApply(price, Array.from(dragTargets.current));
      }
      setDragPrice(null);
      dragTargets.current.clear();
    },
    [dragPrice, onDragApply]
  );

  const handleDragEnd = useCallback(() => {
    setDragPrice(null);
    dragTargets.current.clear();
  }, []);

  const handleMultInput = useCallback(
    (slot: string, raw: string) => {
      onMultChange(slot, raw);
      const n = parseFloat(raw);
      if (!isNaN(n) && n !== 0) onMultiplier(slot, n);
    },
    [onMultiplier, onMultChange]
  );

  const handleFillStart = useCallback(
    (e: React.MouseEvent, rowIdx: number, type: "mult" | "override", value: string) => {
      e.preventDefault();
      e.stopPropagation();
      fillSource.current = { type, value, startIdx: rowIdx };
      fillEndRef.current = rowIdx;
      setFillEndIdx(rowIdx);

      const onMove = (ev: MouseEvent) => {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        if (!el) return;
        const tr = el.closest("tr[data-rowidx]");
        if (tr) {
          const idx = parseInt(tr.getAttribute("data-rowidx") ?? "", 10);
          if (!isNaN(idx)) {
            fillEndRef.current = idx;
            setFillEndIdx(idx);
          }
        }
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (!fillSource.current) return;
        const { type: t, value: v, startIdx } = fillSource.current;
        const endIdx = fillEndRef.current ?? startIdx;
        const lo = Math.min(startIdx, endIdx);
        const hi = Math.max(startIdx, endIdx);
        for (let i = lo; i <= hi; i++) {
          const row = visible[i];
          if (!row) continue;
          if (t === "mult") {
            const f = parseFloat(v);
            onMultChange(row.datetimeUtc, v);
            onMultiplier(row.datetimeUtc, f);
          } else {
            onInlineEdit(row.datetimeUtc, v);
          }
        }
        fillSource.current = null;
        fillEndRef.current = null;
        setFillEndIdx(null);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [visible, onMultiplier, onMultChange, onInlineEdit]
  );

  const getFillRange = useCallback((): [number, number] | null => {
    if (!fillSource.current || fillEndIdx === null) return null;
    const lo = Math.min(fillSource.current.startIdx, fillEndIdx);
    const hi = Math.max(fillSource.current.startIdx, fillEndIdx);
    return [lo, hi];
  }, [fillEndIdx]);

  const thCls =
    "px-3 py-1 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";
  const tdCls = "px-3 py-1.5 text-xs font-mono text-gray-900 dark:text-white";
  const failedTdCls = "px-3 py-1.5 text-xs font-mono text-red-400 dark:text-red-500";

  const filterInputCls =
    "w-full mt-1 px-1.5 py-0.5 text-[10px] font-mono rounded " +
    "bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator " +
    "text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 " +
    "focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all";

  const pillBase =
    "px-1.5 py-0.5 text-[9px] font-semibold rounded cursor-pointer transition-colors";
  const pillActive = "bg-rp-amber text-white";
  const pillInactive =
    "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200";

  return (
    <div
      data-testid="price-table"
      className="overflow-auto h-full border-t border-gray-200 dark:border-rp-separator"
    >
      <table className="w-full text-left">
        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-rp-surface-raised">
          <tr className="border-b border-gray-200 dark:border-rp-separator">
            <th className={thCls} style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll(visibleSlots)}
                className="accent-rp-amber"
                aria-label="Select all rows"
              />
            </th>
            <th className={thCls}>
              Datetime (UTC)
              <input
                type="text"
                className={filterInputCls}
                placeholder="Filter..."
                value={filters.datetime}
                onChange={(e) => setFilter("datetime", e.target.value)}
              />
            </th>
            <th className={thCls}>
              Open
              <input
                type="text"
                className={filterInputCls}
                placeholder="Filter..."
                value={filters.open}
                onChange={(e) => setFilter("open", e.target.value)}
              />
            </th>
            <th className={thCls}>
              High
              <input
                type="text"
                className={filterInputCls}
                placeholder="Filter..."
                value={filters.high}
                onChange={(e) => setFilter("high", e.target.value)}
              />
            </th>
            <th className={thCls}>
              Low
              <input
                type="text"
                className={filterInputCls}
                placeholder="Filter..."
                value={filters.low}
                onChange={(e) => setFilter("low", e.target.value)}
              />
            </th>
            <th className={thCls}>
              Close / Override
              <input
                type="text"
                className={filterInputCls}
                placeholder="Filter..."
                value={filters.close}
                onChange={(e) => setFilter("close", e.target.value)}
              />
            </th>
            <th className={thCls}>Multiplier</th>
            <th className={thCls}>
              <div className="flex items-center gap-0.5 flex-wrap" data-testid="status-filter">
                {STATUS_OPTIONS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter("status", f.key)}
                    className={`${pillBase} ${filters.status === f.key ? pillActive : pillInactive}`}
                    aria-pressed={filters.status === f.key}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row, idx) => {
            const hasPending = !!row.pendingValue;
            const hasAnyOverride = hasPending || row.isOverridden;
            const cellCls = row.isFailed ? failedTdCls : tdCls;
            const isGoodSource = !row.isFailed && !row.isOverridden;
            const dragProps = isGoodSource
              ? {
                  draggable: true,
                  onDragStart: (e: React.DragEvent) =>
                    handleDragStart(e, row.close),
                  title: "Drag to apply this price to failed slots",
                }
              : {};

            const fillRange = getFillRange();
            const inFillRange = fillRange !== null && idx >= fillRange[0] && idx <= fillRange[1];
            const multVal = multValues[row.datetimeUtc] ?? "";
            const hasMultVal = multVal.trim() !== "" && !isNaN(parseFloat(multVal));

            return (
              <tr
                key={row.datetimeUtc}
                data-rowidx={idx}
                className={
                  "border-b border-gray-100 dark:border-rp-separator/50 transition-colors " +
                  (inFillRange
                    ? "bg-blue-500/10 fill-highlight "
                    : row.isSelected
                      ? "bg-rp-amber/5 "
                      : "hover:bg-gray-50 dark:hover:bg-rp-surface-raised/50 ") +
                  (row.isFailed ? "border-l-2 border-l-red-400/40" : "")
                }
                onDragOver={(e) => handleDragOver(e, row.datetimeUtc, row.isFailed)}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
                <td className="px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={row.isSelected}
                    onChange={() => onToggleRow(row.datetimeUtc)}
                    className="accent-rp-amber"
                    aria-label={`Select ${row.datetimeUtc}`}
                  />
                </td>
                <td className={tdCls}>{formatUtc(row.datetimeUtc)}</td>
                <td className={cellCls} {...dragProps}>{fmt2(row.open)}</td>
                <td className={cellCls} {...dragProps}>{fmt2(row.high)}</td>
                <td className={cellCls} {...dragProps}>{fmt2(row.low)}</td>
                <td className="px-3 py-1.5 text-xs font-mono">
                  <span className="inline-flex items-center gap-1.5">
                    <span className={hasAnyOverride ? "line-through decoration-gray-500 text-white" : (row.isFailed ? "text-red-500" : "text-white")} {...dragProps}>
                      {fmt2(row.close)}
                    </span>
                    <input
                      type="text"
                      className={
                        "w-20 px-1.5 py-0.5 text-xs font-mono rounded " +
                        (hasPending
                          ? "bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 font-semibold "
                          : "bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator text-gray-900 dark:text-white ") +
                        "focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                      }
                      placeholder="--"
                      value={row.pendingValue ?? row.overrideValue ?? ""}
                      onChange={(e) => onInlineEdit(row.datetimeUtc, e.target.value)}
                      aria-label={`Override price for ${row.datetimeUtc}`}
                    />
                    {hasPending && (
                      <div
                        className="w-2.5 h-2.5 bg-green-500 rounded-sm cursor-crosshair border border-green-600/60 flex-shrink-0"
                        title="Drag down to fill override price"
                        onMouseDown={(e) => handleFillStart(e, idx, "override", row.pendingValue!)}
                      />
                    )}
                  </span>
                </td>
                <td className="px-3 py-1.5 relative">
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      className={
                        "w-16 px-1.5 py-0.5 text-xs font-mono rounded " +
                        "bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator " +
                        "text-gray-900 dark:text-white focus:outline-none focus:border-rp-amber " +
                        "focus:ring-1 focus:ring-rp-amber transition-all"
                      }
                      placeholder="1.0"
                      value={multVal}
                      onChange={(e) => handleMultInput(row.datetimeUtc, e.target.value)}
                      aria-label={`Multiplier for ${row.datetimeUtc}`}
                    />
                    {hasMultVal && (
                      <div
                        className="w-2.5 h-2.5 bg-rp-amber rounded-sm cursor-crosshair border border-rp-amber/60 flex-shrink-0"
                        title="Drag down to fill"
                        onMouseDown={(e) => handleFillStart(e, idx, "mult", multVal)}
                      />
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${statusDot(row)}`}
                    />
                    {statusLabel(row)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PriceTable;
